import { type FieldErrors, useFormContext } from 'react-hook-form';
import type { MissingFieldsData } from '@/state/navigation';
import {
  type ReferralAgreement,
  useReferralAgreement,
} from '@/views/Referrals/views/ReferralAgreement/ReferralAgreement.helpers';

// Keys and labels for required referral agreement fields
const REQUIRED_FIELDS: Partial<Record<keyof ReferralAgreement, string>> = {
  commissionType: 'Referral Fee',
  commissionValue: 'Referral Fee Amount',
  startDate: 'Start Date',
  endDate: 'End Date',
  termLengthMonths: 'Referral Term Length',
};

// Default form state for when there is no form context
const DEFAULT_FORM_STATE = {
  isValid: true,
  errors: {},
};

function getMissingRequiredFields(
  agreement: ReferralAgreement | undefined,
  errors: FieldErrors<ReferralAgreement>,
) {
  if (!agreement) {
    return [];
  }

  const invalidFields = Object.keys(errors);
  const missingFields = Object.entries(REQUIRED_FIELDS).reduce<
    MissingFieldsData[]
  >((acc, [key, label]) => {
    if (
      !agreement[key as keyof ReferralAgreement] ||
      invalidFields.includes(key as keyof ReferralAgreement)
    ) {
      acc.push({
        section: 'Referral Agreement',
        label,
        name: key,
        index: 0,
        isAddable: false,
        cardId: 'agreement-terms',
      });
    }

    return acc;
  }, []);

  return missingFields;
}

export function useMissingRequiredFields(referralId: string) {
  const { agreement } = useReferralAgreement(referralId);
  const formContext = useFormContext<ReferralAgreement>();
  const { errors } = formContext?.formState ?? DEFAULT_FORM_STATE;

  return {
    missingFields: getMissingRequiredFields(agreement, errors),
    formPath: 'agreement',
  };
}
